/**
 * De dónde sale la marca activa.
 *
 * La fuente real es una DECISIÓN DE PRODUCTO todavía abierta (ver `spec.md` §6).
 * Este archivo existe para que esa decisión no bloquee el wiring: todo pasa por
 * la interfaz `BrandResolver`, y elegir una fuente es pasarle otra
 * implementación a `<BrandProvider>` — un solo lugar, sin tocar el resto.
 *
 * Enchufado en `index.tsx`: `platformBrandResolver` (la marca que elige un
 * superadmin desde Silia OS), luego `envDefaultResolver` (el deploy), luego Silia.
 */

import { DEFAULT_TEMPLATE, TEMPLATES } from './index';
import type { TemplateId } from './index';
import { normalizeThemeId } from './brands/meta';

/** `true` si el string es un TemplateId conocido. */
export const isTemplateId = (value: unknown): value is TemplateId =>
  typeof value === 'string' && Object.prototype.hasOwnProperty.call(TEMPLATES, value);

/**
 * Normaliza cualquier entrada externa a un `TemplateId`.
 *
 * Devuelve `undefined` —no `DEFAULT_TEMPLATE`— cuando no reconoce el valor: un
 * resolver que no sabe tiene que poder decirlo, para que el siguiente de la
 * cadena tenga su turno. El fallback lo aplica `BrandProvider`, una sola vez.
 */
export const normalizeBrandId = (value: unknown): TemplateId | undefined => {
  const normalized = normalizeThemeId(value);
  return isTemplateId(normalized) ? normalized : undefined;
};

export type BrandResolver = {
  /** Nombre para logs y tests. */
  readonly name: string;
  /**
   * Resolución síncrona, disponible en el primer render.
   * Si la fuente es síncrona (subdominio, localStorage), implementar SOLO esta:
   * no hay flash porque la marca correcta está antes del primer pixel.
   */
  resolveSync?: () => TemplateId | undefined;
  /**
   * Resolución asíncrona. Si existe, `BrandProvider` expone `isResolving: true`
   * hasta que resuelva. Puede coexistir con `resolveSync`, que actúa como valor
   * optimista mientras tanto.
   */
  resolve?: () => Promise<TemplateId | undefined>;
};

/** Marca fija. Para tests y Storybook. */
export const staticResolver = (id: TemplateId): BrandResolver => ({
  name: 'static',
  resolveSync: () => id,
});

/**
 * La marca que sale de `REACT_APP_THEME`: la que elige cada deploy (ADR-044).
 *
 * Es el resolver enchufado en `index.tsx`. Un valor que no es una marca
 * conocida (`silia`, vacío) devuelve `undefined` y la app corre la marca por
 * defecto.
 */
export const envDefaultResolver: BrandResolver = {
  name: 'env-default',
  resolveSync: () => normalizeBrandId(process.env.REACT_APP_THEME),
};

/** Dónde guarda el navegador la última marca que respondió la plataforma. */
export const PLATFORM_BRAND_STORAGE_KEY = 'silia.platformBrand';

/** Lo que responde `GET /api/platform/brand`. */
type PlatformBrandResponse = { brand_id?: string; configured?: boolean };

const readStoredBrand = (key: string): TemplateId | undefined => {
  try {
    return normalizeBrandId(window.localStorage.getItem(key));
  } catch {
    return undefined;
  }
};

const storeBrand = (key: string, id: TemplateId | undefined): void => {
  try {
    if (id) window.localStorage.setItem(key, id);
    else window.localStorage.removeItem(key);
  } catch {
    // Modo privado o storage bloqueado: la próxima carga vuelve a preguntar.
  }
};

/**
 * La marca de toda la plataforma: la elige un superadmin desde Silia OS y la
 * sirve `GET /api/platform/brand` (público, porque el login ya se pinta con ella).
 *
 * - `resolveSync` devuelve la última marca que respondió el endpoint, guardada
 *   en localStorage: después de la primera visita no hay flash.
 * - `resolve` pregunta al endpoint. Con `configured: false` devuelve `undefined`
 *   para que el siguiente de la cadena (el deploy, luego Silia) decida.
 * - Si el endpoint falla, se queda con la última marca conocida: un API caído no
 *   cambia la marca de nadie.
 */
export const platformBrandResolver = (
  url: string,
  fetchImpl: typeof fetch = (...args) => fetch(...args),
  storageKey = PLATFORM_BRAND_STORAGE_KEY,
): BrandResolver => ({
  name: 'platform',
  resolveSync: () => readStoredBrand(storageKey),
  resolve: async () => {
    try {
      const response = await fetchImpl(url, { cache: 'no-store' });
      if (!response.ok) return readStoredBrand(storageKey);
      const body = (await response.json()) as PlatformBrandResponse;
      const id = body.configured ? normalizeBrandId(body.brand_id) : undefined;
      storeBrand(storageKey, id);
      return id;
    } catch {
      return readStoredBrand(storageKey);
    }
  },
});

/* ── Las tres candidatas de §6. Ninguna está enchufada todavía. ──────────── */

/**
 * (a) Tenant / cuenta — asíncrono.
 *
 * Único con flash: entre el primer paint y la respuesta del fetch la app pinta
 * la marca por defecto. `resolveSync` lee un valor cacheado para mitigarlo, así
 * que el flash queda solo en el primer login de cada dispositivo.
 */
export const tenantResolver = (
  fetchAccount: () => Promise<{ brandId?: string }>,
  readCachedBrand: () => TemplateId | undefined = () => undefined,
): BrandResolver => ({
  name: 'tenant',
  resolveSync: readCachedBrand,
  resolve: async () => normalizeBrandId((await fetchAccount()).brandId),
});

/**
 * (b) Subdominio — síncrono, sin flash.
 *
 * El hostname está disponible antes de que corra JS, así que la marca correcta
 * llega al primer render. Cuesta un origin por marca (DNS, cert, CloudFront).
 */
export const subdomainResolver = (map: Record<string, TemplateId>): BrandResolver => ({
  name: 'subdomain',
  resolveSync: () => map[window.location.hostname.split('.')[0]],
});

/**
 * (c) Preferencia de usuario — síncrono con persistencia.
 *
 * Sin flash salvo en la primera visita. `localStorage` puede tirar en modo
 * privado o con las cookies bloqueadas, así que la lectura va en try/catch: un
 * resolver que explota dejaría la app sin marca.
 */
export const userPreferenceResolver = (storageKey = 'silia.brand'): BrandResolver => ({
  name: 'user-preference',
  resolveSync: () => {
    try {
      return normalizeBrandId(window.localStorage.getItem(storageKey));
    } catch {
      return undefined;
    }
  },
});

/**
 * Encadena resolvers: gana el primero que devuelva algo.
 *
 * Sirve para el caso realista de §6 (subdominio primario, tenant de fallback)
 * sin que ninguna implementación tenga que conocer a la otra.
 */
export const chainResolvers = (...resolvers: BrandResolver[]): BrandResolver => ({
  name: `chain(${resolvers.map((r) => r.name).join(',')})`,
  resolveSync: () => {
    for (const r of resolvers) {
      const id = r.resolveSync?.();
      if (id) return id;
    }
    return undefined;
  },
  resolve: resolvers.some((r) => r.resolve)
    ? async () => {
        for (const r of resolvers) {
          const id = r.resolve ? await r.resolve() : r.resolveSync?.();
          if (id) return id;
        }
        return undefined;
      }
    : undefined,
});

export { DEFAULT_TEMPLATE };
export type { TemplateId };
